import type { QueryClient } from "@tanstack/react-query";
import { createNanoEvents } from "nanoevents";
import type { ReactNode } from "react";
import type { Location } from "react-router";
import type { BundledTheme, HighlighterCore } from "shiki";
import type { z } from "zod/mini";
import type { HeaderNavigation } from "../../config/validators/HeaderNavigationSchema.js";
import type {
  Navigation,
  ResolvedNavigationRule,
} from "../../config/validators/NavigationSchema.js";
import type {
  CallbackContext,
  ProtectedRouteResult,
  ProtectedRoutesInput,
} from "../../config/validators/ProtectedRoutesSchema.js";
import type {
  AiAssistantsConfig,
  FooterSchema,
} from "../../config/validators/ZudokuConfig.js";
import type { AuthenticationPlugin } from "../authentication/authentication.js";
import { type AuthState, useAuthState } from "../authentication/state.js";
import type { SSRAuthState } from "../components/context/RenderContext.js";
import type { SlotType } from "../components/context/SlotProvider.js";
import { joinUrl } from "../util/joinUrl.js";
import type { MdxComponentsType } from "../util/MdxComponents.js";
import { objectEntries } from "../util/objectEntries.js";
import { matchesAnyProtectedPattern } from "../util/url.js";
import {
  isApiIdentityPlugin,
  isAuthenticationPlugin,
  isEventConsumerPlugin,
  isNavigationPlugin,
  isProfileMenuPlugin,
  needsInitialization,
  type ProfileNavigationItem,
  type ZudokuPlugin,
} from "./plugins.js";

export interface ZudokuEvents {
  location: (event: { from?: Location; to: Location }) => void;
  auth: (auth: { prev: AuthState; next: AuthState }) => void;
}

export interface ApiIdentity {
  authorizeRequest: (request: Request) => Promise<Request> | Request;
  authorizationFields?: {
    headers?: string[];
    queryParams?: string[];
  };
  label: string;
  id: string;
}

export type ApiKeyCache = "api-keys";

type Metadata = Partial<{
  title: string;
  defaultTitle?: string;
  description: string;
  logo: string;
  favicon: string;
  generator: string;
  applicationName: string;
  referrer: string;
  keywords: string[];
  authors: string[];
  creator: string;
  publisher: string;
  robots: string;
}>;

type Site = Partial<{
  dir?: "ltr" | "rtl";
  showPoweredBy?: boolean;
  sidebar?: {
    collapsible?: boolean;
    toggleVisibility?: "always" | "hover";
    togglePosition?: "top" | "center" | "bottom";
  };
  title?: string;
  logo?: {
    src: {
      light: string;
      dark: string;
    };
    width?: string | number;
    height?: string | number;
    alt?: string;
    href?: string;
    reloadDocument?: boolean;
  };
  notFoundPage?: ReactNode;
  banner?: {
    message: ReactNode;
    color?: "note" | "tip" | "info" | "caution" | "danger" | (string & {});
    dismissible?: boolean;
  };
  footer?: z.infer<typeof FooterSchema>;
}>;

type HeaderConfig = {
  navigation?: HeaderNavigation;
  themeSwitcher?: {
    enabled?: boolean;
  };
  placements?: {
    navigation?: "start" | "center" | "end";
    search?: "start" | "center" | "end";
    auth?: "start" | "center" | "end" | "navigation";
  };
};

export type ZudokuContextOptions = {
  basePath?: string;
  canonicalUrlOrigin?: string;
  metadata?: Metadata;
  site?: Site;
  header?: HeaderConfig;
  aiAssistants?: AiAssistantsConfig;
  authentication?: AuthenticationPlugin;
  navigation?: Navigation;
  navigationRules?: ResolvedNavigationRule[];
  plugins?: ZudokuPlugin[];
  slots?: Record<string, SlotType>;
  /**
   * @deprecated Use `slots` instead
   */
  UNSAFE_slotlets?: Record<string, SlotType>;
  mdx?: {
    components?: MdxComponentsType;
  };
  protectedRoutes?: ProtectedRoutesInput;
  syntaxHighlighting?: {
    highlighterPromise: Promise<HighlighterCore>;
    themes?: { light: BundledTheme; dark: BundledTheme };
  };
};

export const normalizeProtectedRoutes = (
  val: ProtectedRoutesInput,
): Record<string, (c: CallbackContext) => ProtectedRouteResult> | undefined => {
  if (!val) return undefined;

  if (Array.isArray(val)) {
    return Object.fromEntries(
      val.map((route) => [
        route,
        (c: CallbackContext) => c.auth.isAuthenticated,
      ]),
    );
  }

  return val;
};

export class ZudokuContext {
  public navigation: Navigation;
  public navigationRules: ResolvedNavigationRule[];
  public readonly authentication?: AuthenticationPlugin;
  public readonly getAuthState: () => AuthState;
  public readonly queryClient: QueryClient;
  public readonly options: ZudokuContextOptions;
  public readonly env: Record<string, string | undefined>;
  public readonly notFoundPage?: ReactNode;
  public readonly protectedRoutes: ReturnType<typeof normalizeProtectedRoutes>;
  private readonly plugins: NonNullable<ZudokuContextOptions["plugins"]>;
  private readonly emitter = createNanoEvents<ZudokuEvents>();
  // Per-request server auth. Zustand store is module-global so we can't
  // mutate it per request.
  readonly ssrAuth?: SSRAuthState;
  readonly initialize: Promise<void> | undefined;

  constructor(
    options: ZudokuContextOptions,
    queryClient: QueryClient,
    env: Record<string, string | undefined>,
    ssrAuth?: SSRAuthState,
  ) {
    this.ssrAuth = ssrAuth;
    this.queryClient = queryClient;
    this.env = env;
    this.options = options;
    this.notFoundPage = options.site?.notFoundPage;
    this.plugins = options.plugins ?? [];
    this.authentication = this.plugins.find(isAuthenticationPlugin);
    this.getAuthState = useAuthState.getState;

    const pluginsToInit = this.plugins.filter(needsInitialization);
    this.initialize =
      pluginsToInit.length > 0
        ? Promise.all(
            pluginsToInit.map((plugin) => plugin.initialize?.(this)),
          ).then(() => {})
        : undefined;

    const pluginProtectedRoutes = Object.fromEntries(
      this.plugins.flatMap((plugin) => {
        if (!isNavigationPlugin(plugin)) return [];
        const routes = plugin.getProtectedRoutes?.();
        if (!routes) return [];

        return Object.entries(normalizeProtectedRoutes(routes) ?? {});
      }),
    );

    this.protectedRoutes = {
      ...pluginProtectedRoutes,
      ...normalizeProtectedRoutes(options.protectedRoutes),
    };

    this.navigation = options.navigation ?? [];
    this.navigationRules = options.navigationRules ?? [];
    this.plugins.forEach((plugin) => {
      if (!isEventConsumerPlugin(plugin)) return;

      objectEntries(plugin.events).forEach(([event, handler]) => {
        // biome-ignore lint/style/noNonNullAssertion: handler is guaranteed to be defined
        this.emitter.on(event, handler!);
      });
    });

    // Client-only: Avoid subscribing in SSR to prevent memory leaks from persistent subscribers.
    if (typeof window !== "undefined") {
      useAuthState.subscribe((state, prevState) => {
        this.emitEvent("auth", {
          prev: prevState,
          next: state,
        });
      });
    }
  }

  getApiIdentities = async () => {
    // Settled individually so one failing plugin can't drop the identities
    // provided by the others.
    const results = await Promise.allSettled(
      this.plugins
        .filter(isApiIdentityPlugin)
        .map((plugin) => plugin.getIdentities(this)),
    );

    return results.flatMap((result) => {
      if (result.status === "rejected") {
        // biome-ignore lint/suspicious/noConsole: Intentional warning
        console.warn(
          "[Zudoku] A plugin failed to provide API identities:",
          result.reason,
        );
        return [];
      }
      return result.value;
    });
  };

  addEventListener<E extends keyof ZudokuEvents>(
    event: E,
    callback: ZudokuEvents[E],
  ) {
    return this.emitter.on(event, callback);
  }

  emitEvent = <E extends keyof ZudokuEvents>(
    event: E,
    ...data: Parameters<ZudokuEvents[E]>
  ) => {
    return this.emitter.emit(event, ...data);
  };

  // Skip plugins for unauthed users on protected paths. Plugins often load
  // protected resources inside getNavigation; that work would leak.
  getPluginNavigation = async (path: string): Promise<Navigation> => {
    if (this.shouldSkipNavigationForProtected(path)) return [];
    const navigations = await Promise.all(
      this.plugins
        .filter(isNavigationPlugin)
        .map((plugin) => plugin.getNavigation?.(joinUrl(path), this)),
    );

    return navigations.flatMap((nav) => nav ?? []);
  };

  private shouldSkipNavigationForProtected(path: string): boolean {
    const patterns = Object.keys(this.protectedRoutes ?? {});
    if (patterns.length === 0) return false;
    if (!matchesAnyProtectedPattern(patterns, path)) return false;
    // Server: per-request ssrAuth. Client: live zustand state.
    const isAuthed =
      typeof window === "undefined"
        ? !!this.ssrAuth?.profile
        : this.getAuthState().isAuthenticated;
    return !isAuthed;
  }

  getProfileMenuItems = () => {
    const accountItems = this.plugins
      .filter((p) => isProfileMenuPlugin(p))
      .flatMap((p) => p.getProfileMenuItems?.(this) ?? [])
      .sort(sortByCategory(["top", "middle", "bottom"]))
      .sort((i) => i.weight ?? 0);

    return accountItems;
  };

  signRequest = async (request: Request) => {
    if (this.ssrAuth?.accessToken) {
      request.headers.set(
        "Authorization",
        `Bearer ${this.ssrAuth.accessToken}`,
      );
      return request;
    }

    if (!this.authentication) {
      throw new Error("No authentication provider configured");
    }

    return await this.authentication.signRequest(request);
  };
}

const sortByCategory =
  (categories: string[]) =>
  (a: ProfileNavigationItem, b: ProfileNavigationItem) => {
    const aIndex = categories.indexOf(a.category ?? "middle");
    const bIndex = categories.indexOf(b.category ?? "middle");

    return aIndex - bIndex;
  };
